/**
 * @file
 * Controls the global mobile main menu.
 */

(function (Drupal, once) {
  'use strict';

  const cleanups = new WeakMap();

  Drupal.behaviors.mobileMainMenu = {
    attach(context) {
      once(
        'mobile-main-menu',
        '.mobile-main-menu__toggle',
        context
      ).forEach((toggle) => {
        const header = toggle.closest('.site-header');
        const menu = document.getElementById(
          toggle.getAttribute('aria-controls')
        );

        if (!header || !menu) {
          return;
        }

        const controller = new AbortController();
        const listenerOptions = { signal: controller.signal };
        // CSS owns the component breakpoint, including the scroll behavior.
        const isCompactViewport = () => getComputedStyle(toggle).display !== 'none';
        let compactFrame = null;
        let compact = false;

        const updateCompactHeader = () => {
          compactFrame = null;
          if (!isCompactViewport()) {
            compact = false;
          }
          else if (!menu.hidden) {
            // Keep the open overlay's top edge stable while the page scrolls.
            return;
          }
          else if (window.scrollY <= 4) {
            compact = false;
          }
          else if (window.scrollY >= 80) {
            compact = true;
          }
          // The 76px dead band exceeds the 40px banner padding change, so
          // scroll anchoring cannot immediately reverse the state.
          header.classList.toggle('site-header--compact', compact);
        };
        const scheduleCompactHeader = () => {
          if (compactFrame === null) {
            compactFrame = requestAnimationFrame(updateCompactHeader);
          }
        };
        window.addEventListener('scroll', scheduleCompactHeader, {
          ...listenerOptions, passive: true,
        });
        // Restore the state for back/forward navigation and restored scrolling.
        window.addEventListener('pageshow', scheduleCompactHeader, listenerOptions);

        const noticeSelector = '#klaro .cookie-notice:not(.cookie-modal-notice)';
        let notice = null;

        // Observe only while open. Klaro may replace or hide its notice after
        // a decision; its height also changes with wrapping and font loading.
        const updateNoticeHeight = () => {
          const current = document.querySelector(noticeSelector);
          if (current !== notice) {
            noticeResizeObserver.disconnect();
            notice = current;
            if (notice) noticeResizeObserver.observe(notice);
          }
          const height = notice && getComputedStyle(notice).visibility !== 'hidden'
            ? notice.getBoundingClientRect().height : 0;
          menu.style.setProperty('--mobile-menu-notice-height', `${height}px`);
        };
        const noticeResizeObserver = new ResizeObserver(updateNoticeHeight);
        const noticeObserver = new MutationObserver(updateNoticeHeight);
        const observeNotice = () => {
          const root = document.getElementById('klaro');
          if (root) {
            noticeObserver.observe(root, {
              childList: true, subtree: true, attributes: true,
              attributeFilter: ['class', 'style', 'hidden'],
            });
          }
          updateNoticeHeight();
        };
        // Also handle a consent container inserted after the menu opens.
        const noticeRootObserver = new MutationObserver(observeNotice);

        /**
         * Store the current mobile header height as a CSS custom property.
         *
         * This keeps the open menu height in sync with the actual rendered
         * header instead of relying on a hard-coded value.
         */
        let headerHeight = null;
        const storeHeaderHeight = (height) => {
          if (height === headerHeight) return;
          headerHeight = height;
          document.documentElement.style.setProperty('--mobile-site-header-height', `${height}px`);
        };
        const updateHeaderHeight = () => {
          storeHeaderHeight(header.getBoundingClientRect().height);
        };

        const openMenu = () => {
          header.dispatchEvent(new CustomEvent('adocs:header-panel-open', { detail: toggle }));
          updateHeaderHeight();

          menu.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
          header.classList.add('mobile-main-menu-is-open');
          document.body.classList.add('mobile-main-menu-is-open');
          observeNotice();
          noticeRootObserver.observe(document.body, { childList: true });
        };

        const closeMenu = () => {
          menu.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          header.classList.remove('mobile-main-menu-is-open');
          document.body.classList.remove('mobile-main-menu-is-open');
          noticeObserver.disconnect();
          noticeRootObserver.disconnect();
          noticeResizeObserver.disconnect();
          notice = null;
          menu.style.removeProperty('--mobile-menu-notice-height');
          scheduleCompactHeader();
        };

        toggle.addEventListener('click', () => {
          const isOpen =
            toggle.getAttribute('aria-expanded') === 'true';

          if (isOpen) {
            closeMenu();
          }
          else {
            openMenu();
          }
        }, listenerOptions);

        header.addEventListener('adocs:header-panel-open', (event) => {
          if (event.detail !== toggle) closeMenu();
        }, listenerOptions);

        // This is a nonmodal navigation: close when focus moves back to the page.
        document.addEventListener('focusin', (event) => {
          if (!menu.hidden && !menu.contains(event.target) && !toggle.contains(event.target)
            && !event.target.closest(noticeSelector)) {
            closeMenu();
          }
        }, listenerOptions);

        window.addEventListener('resize', () => {
          if (!isCompactViewport() && !menu.hidden) {
            const restoreFocus = menu.contains(document.activeElement) || document.activeElement === toggle;
            closeMenu();
            if (restoreFocus) header.querySelector('.menu-logo-link')?.focus();
          }
          scheduleCompactHeader();
        }, listenerOptions);

        // Include animated padding so the overlay and sticky controls follow
        // the banner throughout scrolling and search-panel transitions.
        const headerObserver = new ResizeObserver((entries) => {
          const entry = entries.find((entry) => entry.target === header);
          const size = entry?.borderBoxSize?.[0];
          // The observer already measured the border box, including animated
          // padding. Do not force a second layout read in its callback.
          if (size) storeHeaderHeight(size.blockSize);
          else updateHeaderHeight();
        });
        headerObserver.observe(header, { box: 'border-box' });

        document.addEventListener('keydown', (event) => {
          if (
            event.key === 'Escape'
            && toggle.getAttribute('aria-expanded') === 'true'
          ) {
            closeMenu();
            toggle.focus();
          }
        }, listenerOptions);

        cleanups.set(toggle, () => {
          closeMenu();
          controller.abort();
          headerObserver.disconnect();
          cancelAnimationFrame(compactFrame);
          header.classList.remove('site-header--compact');
        });

        updateCompactHeader();
        updateHeaderHeight();
      });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      once.remove('mobile-main-menu', '.mobile-main-menu__toggle', context).forEach((toggle) => {
        cleanups.get(toggle)?.();
        cleanups.delete(toggle);
      });
    },
  };
})(Drupal, once);
